import { SearchTagsSidebar } from '@/components/search/search-sidebar';
import { RightTagSearchSidebar } from '@/components/search/tag-search-sidebar';
import { bookDb } from '@/db/book';
import { tagsDb } from '@/db/tags';
import { getModernAuthoringJsonFiles } from '@/lib/zybooks-api';
import { Outlet } from 'react-router-dom';

export async function searchLoader() {
    const urlMap = new Map<string, string>();
    try {
        const response = await getModernAuthoringJsonFiles();
        if (response.success && Array.isArray(response.urls)) {
            for (const item of response.urls) {
                if (item.key && item.url) {
                    urlMap.set(item.key, item.url);
                }
            }
        }
    } catch (err) {
        console.warn('Failed to fetch presigned URLs', err);
    }

    const getFileUrl = (filename: string): string => {
        return urlMap.get(filename) || '';
    };

    const tagsPromise = (async () => {
        if ((await tagsDb.tags.count()) === 0 || localStorage.getItem('seedVersion') !== 'v1') {
            const res = await fetch(getFileUrl('db-seed.json'));
            const seed = await res.json();
            await tagsDb.transaction('rw', tagsDb.tags, async () => {
                await tagsDb.tags.bulkPut(seed.tags);
                localStorage.setItem('seedVersion', 'v1');
            });
        }
    })();

    const booksPromise = (async () => {
        if ((await bookDb.books.count()) === 0 || localStorage.getItem('bookSeedVersion') !== 'v1') {
            const res = await fetch(getFileUrl('book-db-seed.json'));
            const raw = await res.json();
            type BookSeedMap = Record<
                string,
                {
                    children: { zytags: string[]; htmlTags: string[] };
                    gdocs: string[];
                }
            >;
            const booksToPut = Array.isArray((raw as any)?.books)
                ? (raw as any).books
                : Object.entries(raw as BookSeedMap).map(([bookName, data]) => ({
                      bookName,
                      ...data,
                  }));
            await bookDb.transaction('rw', bookDb.books, async () => {
                await bookDb.books.bulkPut(booksToPut);
                localStorage.setItem('bookSeedVersion', 'v1');
            });
        }
    })();

    const relationshipTagsPromise = (async () => {
        if ((await tagsDb.relationshipTags.count()) === 0 || localStorage.getItem('relationshipTagSeedVersion') !== 'v1') {
            try {
                const groups = ['A-E', 'F-J', 'K-O', 'P-T', 'U-Z'];
                const recordsMap = new Map<string, { id: string; parent: string; child: string; entries: any[] }>();

                for (const group of groups) {
                    try {
                        const res = await fetch(getFileUrl(`parent-child-index-${group}.json`));
                        if (!res.ok) continue;
                        const index = await res.json();

                        for (const [parent, childrenMap] of Object.entries(index)) {
                            for (const [child, entries] of Object.entries(childrenMap as Record<string, any[]>)) {
                                if (!Array.isArray(entries) || entries.length === 0) continue;

                                const id = `${parent.toLowerCase()}:${child.toLowerCase()}`;
                                const record = {
                                    id,
                                    parent: parent.toLowerCase(),
                                    child: child.toLowerCase(),
                                    entries: entries,
                                };

                                // Merge entries if parent-child pair already exists, preferring different books
                                const existing = recordsMap.get(id);
                                if (existing) {
                                    const seenBooks = new Set(existing.entries.map((e: any) => e.book));
                                    const merged = [...existing.entries];
                                    for (const entry of entries) {
                                        if (merged.length >= 2) break;
                                        if (!seenBooks.has(entry.book)) {
                                            merged.push(entry);
                                            seenBooks.add(entry.book);
                                        }
                                    }
                                    record.entries = merged.slice(0, 2);
                                }

                                recordsMap.set(id, record);
                            }
                        }
                    } catch (err) {
                        console.warn(`Failed to load parent-child-index-${group}.json:`, err);
                    }
                }

                if (recordsMap.size > 0) {
                    const records = Array.from(recordsMap.values());
                    await tagsDb.transaction('rw', tagsDb.relationshipTags, async () => {
                        await tagsDb.relationshipTags.bulkPut(records);
                        localStorage.setItem('relationshipTagSeedVersion', 'v1');
                    });
                }
            } catch (err) {
                console.warn('Failed to load parent-child-index files:', err);
            }
        }
    })();

    const snippetIndexPromise = (async () => {
        if ((await tagsDb.snippetIndex.count()) === 0 || localStorage.getItem('snippetIndexSeedVersion') !== 'v1') {
            try {
                const groups = ['A-B', 'C-D', 'E-F', 'G-H', 'I-J', 'K-L', 'M-N', 'O-P', 'Q-R', 'S-T', 'U-Z', 'Other'];

                const records: { id: string; snippet: string }[] = [];

                for (const group of groups) {
                    try {
                        const res = await fetch(getFileUrl(`zytag-snippets-${group}.json`));
                        if (!res.ok) continue;
                        const index = await res.json();
                        // Structure: { "<bookName>:<id>:<chapter>": { snippet: string } }
                        for (const [key, value] of Object.entries(index as Record<string, { snippet?: string }>)) {
                            const snippet = value?.snippet;
                            if (!snippet) continue;
                            records.push({ id: key, snippet });
                        }
                    } catch (err) {
                        console.warn(`Failed to load zytag-snippets-${group}.json:`, err);
                    }
                }

                if (records.length > 0) {
                    await tagsDb.transaction('rw', tagsDb.snippetIndex, async () => {
                        await tagsDb.snippetIndex.bulkPut(records);
                        localStorage.setItem('snippetIndexSeedVersion', 'v1');
                    });
                }
            } catch (err) {
                console.warn('Failed to load zytag-snippets files:', err);
            }
        }
    })();

    return { tagsPromise, booksPromise, relationshipTagsPromise, snippetIndexPromise };
}

export default function Search() {
    return (
        <div className="flex min-h-0 min-w-0 h-full flex-1 overflow-hidden md:flex-row flex-col">
            <SearchTagsSidebar />
            <div className="min-h-0 min-w-0 flex-1">
                <div className="mx-auto max-w-[1280px] px-2 py-2 text-center">
                    <Outlet />
                </div>
            </div>
            <RightTagSearchSidebar />
        </div>
    );
}
